import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import WalletConnectModal from '../components/WalletConnectModal';
import WalletMenu from '../components/WalletMenu';
import { Shield } from 'lucide-react';
import '../styles/jp-morgan.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardJP = () => {
  const { user, token, logout } = useAuth();
  const { account, bethBalance, walletType } = useWeb3();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [treasurySummary, setTreasurySummary] = useState(null);
  const [collateralStats, setCollateralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showWalletModal, setShowWalletModal] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      // Fetch protocol stats (includes value lock data)
      const statsRes = await fetch(`${API}/protocol/stats`);
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch treasury summary
      const treasuryRes = await fetch(`${API}/credit-enhancement/treasury-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (treasuryRes.ok) {
        setTreasurySummary(await treasuryRes.json());
      }
      
      // Fetch platform collateral positions
      const collateralRes = await fetch(`${API}/admin/collateral/positions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (collateralRes.ok) {
        const data = await collateralRes.json();
        setCollateralStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  const navItems = [
    { id: 'overview', label: 'Overview', path: '/admin-dashboard' },
    { id: 'customers', label: 'Client Ledger', path: '/customers' },
    { id: 'applications', label: 'Applications', path: '/investor-applications' },
    { id: 'accreditation', label: 'Accreditation', path: '/accreditation-admin' },
    { id: 'credit', label: 'Credit Enhancement', path: '/credit-enhancement' },
    { id: 'securities', label: 'Securities Lending', path: '/securities-transactions' },
    { id: 'beth-finance', label: 'BETH Finance', path: '/admin/beth-finance' },
    { id: 'partner-lenders', label: 'Partner Lenders', path: '/admin/partner-lenders' },
    { id: 'lending', label: 'Lending Reserve', path: '/lending-reserve' },
    { id: 'treasury', label: 'Treasury', path: '/treasury' },
    { id: 'reports', label: 'Reports', path: '/reports' },
    { id: 'transactions', label: 'Transactions', path: '/transactions' },
    { id: 'market', label: 'Market Data', path: '/market' },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #050d18 100%)' }}>
      {/* Top Header Bar */}
      <header className="border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)', background: '#0a1628' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #b8860b 0%, #8b6914 100%)' }}>
                <span className="text-[#0a1628] font-bold text-lg">B</span>
              </div>
              <div>
                <h1 className="text-white text-lg font-light tracking-wide">BETHEREUM</h1>
                <p className="text-xs" style={{ color: '#6c757d', letterSpacing: '0.1em' }}>PRIVATE WEALTH MANAGEMENT</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {account ? (
              <WalletMenu onSwitchWallet={() => setShowWalletModal(true)} />
            ) : (
              <button
                onClick={() => setShowWalletModal(true)}
                className="px-4 py-2 text-xs font-medium tracking-wider"
                style={{ 
                  background: 'linear-gradient(180deg, #b8860b 0%, #9a7209 100%)',
                  color: '#0a1628',
                  border: 'none',
                  borderRadius: '2px'
                }}
                data-testid="admin-connect-wallet-btn"
              >
                CONNECT WALLET
              </button>
            )}
            
            <div className="text-right">
              <p className="text-xs" style={{ color: '#6c757d', letterSpacing: '0.05em' }}>ADMINISTRATOR</p>
              <p className="text-white text-sm">{user?.name || user?.email?.split('@')[0]}</p>
            </div>
            
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="px-3 py-1 text-xs"
              style={{ color: '#b8860b', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '2px' }}
              data-testid="admin-logout-btn"
            >
              SIGN OUT
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)', background: 'rgba(10, 22, 40, 0.8)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className="px-4 py-3 text-xs font-medium tracking-wider transition-colors"
                style={{ 
                  color: activeTab === item.id ? '#b8860b' : '#6c757d',
                  borderBottom: activeTab === item.id ? '2px solid #b8860b' : '2px solid transparent'
                }}
                onClick={() => setActiveTab(item.id)}
              >
                {item.label.toUpperCase()}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-light text-white tracking-wide">Administrative Dashboard</h2>
          <p className="text-sm mt-1" style={{ color: '#6c757d' }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-testid="admin-key-metrics">
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium tracking-wider" style={{ color: '#6c757d' }}>TOTAL VALUE LOCKED</p>
              {stats?.is_live ? (
                <span className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400">LIVE</span>
              ) : (
                <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400">TARGET</span>
              )}
            </div>
            <p className="text-2xl font-light text-white">{formatNumber(stats?.total_value_locked || 100000000000)} BETH</p>
            <p className="text-xs mt-2" style={{ color: '#b8860b' }}>${formatNumber(stats?.total_value_locked_usd || 100000000)} USD</p>
          </div>
          
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <p className="text-xs font-medium tracking-wider mb-2" style={{ color: '#6c757d' }}>ACTIVE CREDIT LINES</p>
            <p className="text-2xl font-light text-white">{stats?.activeCreditLines || 0}</p>
            <p className="text-xs mt-2" style={{ color: '#6c757d' }}>Across all tiers</p>
          </div>
          
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <p className="text-xs font-medium tracking-wider mb-2" style={{ color: '#6c757d' }}>GUARANTEES ISSUED</p>
            <p className="text-2xl font-light text-white">{treasurySummary?.active_guarantees || 0}</p>
            <p className="text-xs mt-2" style={{ color: '#b8860b' }}>{formatCurrency(treasurySummary?.total_usd_equivalent || 0)} committed</p>
          </div>
          
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <p className="text-xs font-medium tracking-wider mb-2" style={{ color: '#6c757d' }}>TREASURY BETH</p>
            <p className="text-2xl font-light text-white">{formatNumber(parseFloat(bethBalance) || 0)}</p>
            <p className="text-xs mt-2" style={{ color: '#6c757d' }}>Available for guarantees</p>
          </div>
        </div>
        
        {/* Platform Collateral Stats */}
        <div className="p-6 mb-8" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }} data-testid="platform-collateral-stats">
          <h3 className="text-sm font-medium tracking-wider mb-4" style={{ color: '#b8860b' }}>CREDIT FACILITY - PLATFORM COLLATERAL OVERVIEW</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="p-4 text-center" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>TOTAL POSITIONS</p>
              <p className="text-2xl font-light text-blue-400">{collateralStats?.total_positions || 0}</p>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>ACTIVE POSITIONS</p>
              <p className="text-2xl font-light text-green-400">{collateralStats?.active_positions || 0}</p>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>TOTAL COLLATERAL</p>
              <p className="text-2xl font-light text-purple-400">{formatNumber(collateralStats?.total_collateral_beth || 0)}</p>
              <p className="text-xs mt-1" style={{ color: '#6c757d' }}>BETH</p>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(236, 72, 153, 0.1)', border: '1px solid rgba(236, 72, 153, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>CREDIT UTILIZED</p>
              <p className="text-2xl font-light text-pink-400">{formatNumber(collateralStats?.total_credit_utilized_beth || 0)}</p>
              <p className="text-xs mt-1" style={{ color: '#6c757d' }}>BETH</p>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>PLATFORM LTV</p>
              <p className={`text-2xl font-light ${(collateralStats?.platform_ltv || 0) > 60 ? 'text-red-400' : (collateralStats?.platform_ltv || 0) > 40 ? 'text-yellow-400' : 'text-green-400'}`}>
                {collateralStats?.platform_ltv || 0}%
              </p>
              <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Avg. health</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Credit Enhancement Quick Actions */}
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <h3 className="text-sm font-medium tracking-wider mb-4" style={{ color: '#b8860b' }}>CREDIT ENHANCEMENT</h3>
            <div className="space-y-3">
              <Link
                to="/credit-enhancement"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Create Enhancement Report</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Generate credit enhancement documentation</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/credit-enhancement"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Create PAS Agreement</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Private Accord & Satisfaction</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/credit-enhancement"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">View Active Guarantees</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Manage conditional BETH guarantees</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
            </div>
          </div>

          {/* Treasury Quick Actions */}
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <h3 className="text-sm font-medium tracking-wider mb-4" style={{ color: '#b8860b' }}>TREASURY MANAGEMENT</h3>
            <div className="space-y-3">
              <Link
                to="/treasury-securities"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '2px' }}
                data-testid="treasury-securities-link"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Treasury Index Securities</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Secured investment products with cost sheets</p>
                  </div>
                  <span style={{ color: '#3b82f6' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/treasury"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Subscription Management</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>View and manage platform subscriptions</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/distribute"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Token Distribution</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Send BETH to investors</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/market"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Market Data & Pricing</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>View ETH/BETH prices and oracle data</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Novation Management Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Finance Orders Management */}
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <h3 className="text-sm font-medium tracking-wider mb-4" style={{ color: '#b8860b' }}>FINANCE AGREEMENTS</h3>
            <div className="space-y-3">
              <Link
                to="/admin/finance-orders"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '2px' }}
                data-testid="admin-finance-orders-link"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">All Finance Orders</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>View all TIAO & TIMO finance agreements</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/admin/novation-dashboard"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '2px' }}
                data-testid="admin-novation-dashboard-link"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Novation Dashboard</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Manage Auto & Mortgage Novation/Refinance</p>
                  </div>
                  <span style={{ color: '#8b5cf6' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/new-auto-financing"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.3)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">New Auto Finance (TIAO)</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Create new vehicle financing</p>
                  </div>
                  <span style={{ color: '#22d3ee' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/auto-refinance"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Auto Loan Refinance</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Refinance existing auto loans</p>
                  </div>
                  <span style={{ color: '#a855f7' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/new-mortgage-financing"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">New Mortgage Finance (TIMO)</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Create new property financing</p>
                  </div>
                  <span style={{ color: '#6366f1' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/mortgage-refinance"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Mortgage Refinance</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Refinance existing mortgages</p>
                  </div>
                  <span style={{ color: '#8b5cf6' }}>→</span>
                </div>
              </Link>
            </div>
          </div>
          
          {/* Investor Applications Quick Access */}
          <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
            <h3 className="text-sm font-medium tracking-wider mb-4" style={{ color: '#b8860b' }}>CLIENT MANAGEMENT</h3>
            <div className="space-y-3">
              <Link
                to="/investor-applications"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(249, 115, 22, 0.1)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Investor Applications</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Review & approve new investor applications</p>
                  </div>
                  <span style={{ color: '#f97316' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/admin/investor-inquiries"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '2px' }}
                data-testid="admin-investor-inquiries-link"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Investor Inquiries</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Manage leads from the investors page</p>
                  </div>
                  <span style={{ color: '#3b82f6' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/customers"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Client Ledger</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>View all registered clients</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
              
              <Link
                to="/accreditation-admin"
                className="block p-4 transition-colors"
                style={{ background: 'rgba(184, 134, 11, 0.05)', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm">Accreditation Management</p>
                    <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Manage investor accreditation status</p>
                  </div>
                  <span style={{ color: '#b8860b' }}>→</span>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Contract Addresses */}
        <div className="p-6 mb-8" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
          <h3 className="text-sm font-medium tracking-wider mb-4" style={{ color: '#b8860b' }}>VALUE LOCK ALLOCATION</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 text-center" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>COLLATERAL VAULT</p>
              <p className="text-2xl font-light text-blue-400">{formatNumber(stats?.collateral_backing || 50000000000)}</p>
              <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Credit facility backing</p>
              <a href="https://etherscan.io/address/0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d" target="_blank" rel="noopener noreferrer" className="text-xs mt-2 inline-block" style={{ color: '#b8860b' }}>View on Etherscan →</a>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>TREASURY RESERVE</p>
              <p className="text-2xl font-light text-green-400">{formatNumber(stats?.treasury_reserve || 30000000000)}</p>
              <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Platform operational reserves</p>
              <a href="https://etherscan.io/address/0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c" target="_blank" rel="noopener noreferrer" className="text-xs mt-2 inline-block" style={{ color: '#b8860b' }}>View on Etherscan →</a>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>PLATFORM RESERVE</p>
              <p className="text-2xl font-light text-[#b8860b]">{formatNumber(stats?.liquidity_pool || 20000000000)}</p>
              <p className="text-xs mt-1" style={{ color: '#6c757d' }}>Operational reserves</p>
              <a href="https://etherscan.io/address/0x8F01D20feaA323049976f35865F43D3C457F19CF" target="_blank" rel="noopener noreferrer" className="text-xs mt-2 inline-block" style={{ color: '#b8860b' }}>View on Etherscan →</a>
            </div>
          </div>
          <div className="p-4" style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '4px' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs tracking-wider" style={{ color: '#6c757d' }}>TOTAL VALUE LOCKED</p>
                <p className="text-3xl font-light text-white mt-1">{formatNumber(stats?.total_value_locked || 100000000000)} <span style={{ color: '#b8860b' }}>BETH</span></p>
              </div>
              <div className="text-right">
                <p className="text-xs tracking-wider" style={{ color: '#6c757d' }}>USD EQUIVALENT</p>
                <p className="text-xl font-light" style={{ color: '#b8860b' }}>${formatNumber(stats?.total_value_locked_usd || 100000000)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Deployed Contracts */}
        <div className="p-6" style={{ background: '#1a2d4a', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: '2px' }}>
          <h3 className="text-sm font-medium tracking-wider mb-4" style={{ color: '#b8860b' }}>DEPLOYED CONTRACTS</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'BETH Token', address: '0x6822824c5349bf005e353ec5dda3fa34595ea811' },
              { name: 'Collateral Vault', address: '0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d' },
              { name: 'Credit Line', address: '0x8abfe72db538c6ff99452a910b9bc7a980ef0fdd' },
              { name: 'Liquidation Engine', address: '0x65e67b63513de0b2bf463c47a2a284254e23fcda' },
              { name: 'Treasury Manager', address: '0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c' },
            ].map((contract) => (
              <div key={contract.name} className="p-3" style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '2px' }}>
                <p className="text-xs tracking-wider mb-1" style={{ color: '#6c757d' }}>{contract.name.toUpperCase()}</p>
                <a
                  href={`https://etherscan.io/address/${contract.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono hover:underline"
                  style={{ color: '#b8860b' }}
                >
                  {contract.address.slice(0, 10)}...{contract.address.slice(-8)}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Chainlink Oracle Data Section */}
        <div className="p-6 mt-6" style={{ background: '#1a2d4a', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '2px' }} data-testid="chainlink-oracle-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium tracking-wider" style={{ color: '#3b82f6' }}>CHAINLINK ORACLE DATA (LIVE)</h3>
            <span className="px-2 py-1 text-xs rounded" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>● LIVE</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 text-center" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>ETH/USD</p>
              <p className="text-xl font-light text-blue-400">--</p>
              <a href="https://etherscan.io/address/0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419" target="_blank" rel="noopener noreferrer" className="text-xs mt-2 inline-block" style={{ color: '#3b82f6' }}>Feed →</a>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(249, 115, 22, 0.1)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>BTC/USD</p>
              <p className="text-xl font-light text-orange-400">--</p>
              <a href="https://etherscan.io/address/0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c" target="_blank" rel="noopener noreferrer" className="text-xs mt-2 inline-block" style={{ color: '#f97316' }}>Feed →</a>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>LINK/USD</p>
              <p className="text-xl font-light text-blue-400">--</p>
              <a href="https://etherscan.io/address/0x2c1d072e956AFFC0D435Cb7AC38EF18d24d9127c" target="_blank" rel="noopener noreferrer" className="text-xs mt-2 inline-block" style={{ color: '#3b82f6' }}>Feed →</a>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>BETH/USD</p>
              <p className="text-xl font-light" style={{ color: '#b8860b' }}>$1.00</p>
              <p className="text-xs mt-2" style={{ color: '#6c757d' }}>Treasury Pegged</p>
            </div>
          </div>
          <p className="text-xs mt-4 text-center" style={{ color: '#6c757d' }}>Prices fetched from Chainlink decentralized oracle network on Ethereum Mainnet</p>
        </div>

        {/* Subscription Revenue Section */}
        <div className="p-6" style={{ background: 'linear-gradient(180deg, #0d1d35 0%, #0a1628 100%)', border: '1px solid rgba(184, 134, 11, 0.2)', borderRadius: '4px' }}>
          <h3 className="text-lg font-light text-white mb-4" style={{ letterSpacing: '1px' }}>SUBSCRIPTION REVENUE</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 text-center" style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>BASIC TIER</p>
              <p className="text-xl font-light text-white">$99/month</p>
              <p className="text-xs mt-1" style={{ color: '#b8860b' }}>99 BETH credits</p>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>PROFESSIONAL</p>
              <p className="text-xl font-light text-white">$499/month</p>
              <p className="text-xs mt-1" style={{ color: '#b8860b' }}>499 BETH credits</p>
            </div>
            <div className="p-4 text-center" style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)', borderRadius: '4px' }}>
              <p className="text-xs tracking-wider mb-2" style={{ color: '#6c757d' }}>ENTERPRISE</p>
              <p className="text-xl font-light text-white">$2,500/month</p>
              <p className="text-xs mt-1" style={{ color: '#b8860b' }}>2,500 BETH credits</p>
            </div>
          </div>
          <div className="p-4" style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '4px' }}>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 mt-0.5" style={{ color: '#b8860b' }} />
              <div>
                <p className="text-white text-sm mb-1">Payment Processing</p>
                <p className="text-xs" style={{ color: '#6c757d' }}>
                  All USD payments are processed via Stripe. Funds are deposited directly to your connected bank account. 
                  Configure payouts in <a href="https://dashboard.stripe.com/settings/payouts" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#b8860b' }}>Stripe Dashboard → Settings → Payouts</a>.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="flex-1">
              <button className="w-full px-4 py-2 text-sm" style={{ background: 'linear-gradient(180deg, #635bff 0%, #5046e5 100%)', color: 'white', border: 'none', borderRadius: '4px' }}>
                Open Stripe Dashboard
              </button>
            </a>
            <Link to="/admin/finance-orders" className="flex-1">
              <button className="w-full px-4 py-2 text-sm" style={{ background: 'transparent', color: '#b8860b', border: '1px solid rgba(184, 134, 11, 0.5)', borderRadius: '4px' }}>
                View Subscriptions
              </button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12 py-6" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-xs" style={{ color: '#6c757d' }}>
            © 2026 Bethereum Private Wealth Management. All rights reserved.
          </p>
          <p className="text-xs" style={{ color: '#6c757d' }}>
            Accredited Investors Only • Reg D 506(b) Exempt
          </p>
        </div>
      </footer>

      {/* Wallet Connect Modal */}
      <WalletConnectModal 
        isOpen={showWalletModal} 
        onClose={() => setShowWalletModal(false)} 
      />
    </div>
  );
};

export default AdminDashboardJP;
